/**
 * TeamLeader Exam Management Workspace — Students At Risk page.
 *
 * The full, paginated view behind the compact top-5 widget on /team-leader/exams. Backed by
 * `GET /teamleader/exams/at-risk`, which the server always scopes to the caller's managed
 * groups/students (CourseId/GroupId narrow that scope — they can never widen it).
 *
 * Risk model (mirrors the thresholds in TeamLeaderExamService — kept in sync by hand, they are
 * not exposed by the API):
 *   - Failing Grades   → >= 2 failed completed sessions in the last 90 days
 *   - Low Activity     → no LastSeenAt/LastLoginDate, or older than 14 days
 *   - Checkpoint Risk  → >= 1 enrollment with CheckpointStatus == Failed
 *   - Violations       → total ExamSession.ViolationCount >= 3
 * `riskScore` is simply the count of true flags (0-4) — unweighted by design, and what the
 * server sorts on (descending).
 *
 * Server-side facets: course, group, minimum risk flags, paging. The endpoint has no search or
 * per-flag facet, so the name/email box and the flag chips below filter the CURRENT PAGE only —
 * the UI labels them as such rather than pretending they are server-wide.
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTlAtRiskStudents } from "@/hooks/use-tl-exams";
import type { TlAtRiskStudent, TlAtRiskParams } from "@/lib/api/tl-exams";
import { useMyGroupContent, useMyGroups } from "@/hooks/use-teamleader";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { PaginationControls } from "@/components/PaginationControls";
import { TableSkeleton } from "@/components/loading";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ShieldAlert,
  ShieldCheck,
  Search,
  FilterX,
  RotateCcw,
  ChevronRight,
  ChevronLeft,
  Users,
  Layers,
  AlertTriangle,
} from "lucide-react";

/**
 * Optional deep-link context, carried over by the dashboard widget's "View all" button so the
 * page opens under the same course/group narrowing the user was already looking at. Follows the
 * hand-rolled `validateSearch` pattern used by routes/_authenticated/exams.tsx (no zod here).
 */
interface AtRiskSearch {
  courseId?: string;
  groupId?: string;
}

export const Route = createFileRoute("/_authenticated/team-leader/exams_/at-risk")({
  validateSearch: (search: Record<string, unknown>): AtRiskSearch => ({
    courseId: (search.courseId as string | undefined) || undefined,
    groupId: (search.groupId as string | undefined) || undefined,
  }),
  component: TeamLeaderStudentsAtRiskPage,
});

// ── Risk flags ────────────────────────────────────────────────────────────────
const RISK_FLAGS: Array<{
  key: keyof Pick<
    TlAtRiskStudent,
    "failingGradesFlag" | "lowActivityFlag" | "checkpointRiskFlag" | "violationRiskFlag"
  >;
  label: string;
  hint: string;
}> = [
  { key: "failingGradesFlag", label: "Failing Grades", hint: "≥ 2 failed attempts in 90 days" },
  { key: "lowActivityFlag", label: "Low Activity", hint: "No activity in 14+ days" },
  { key: "checkpointRiskFlag", label: "Checkpoint Risk", hint: "≥ 1 failed checkpoint" },
  { key: "violationRiskFlag", label: "Violations", hint: "≥ 3 exam violations" },
];

const MIN_FLAG_OPTIONS = [
  { value: "1", label: "1+ risk flag" },
  { value: "2", label: "2+ risk flags" },
  { value: "3", label: "3+ risk flags" },
  { value: "4", label: "All 4 risk flags" },
] as const;

function riskScoreClass(score: number): string {
  if (score >= 3) return "bg-destructive/15 text-destructive border-destructive/30";
  if (score === 2) return "bg-warning/15 text-warning border-warning/30";
  return "bg-muted text-muted-foreground border-border";
}

// ── Formatting helpers (mirrors the local formatters in exams.tsx — no shared util exists) ──
function formatPercent(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v)}%`;
}

function formatCount(v: number | null | undefined): string {
  return v === null || v === undefined ? "0" : String(v);
}

function formatDate(v: string | null | undefined): string {
  if (!v) return "Never";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ── Summary cards ─────────────────────────────────────────────────────────────
// Each card is its own `totalCount`-only probe (pageSize 1) at a different MinRiskFlags
// threshold — the endpoint returns no flag breakdown, and counting the current page would
// under-report. They follow the course/group context but ignore the page's own flag filter so
// the totals stay stable while the user pages/filters below.
function RiskSummaryCard({
  label,
  hint,
  minRiskFlags,
  courseId,
  groupId,
  icon: Icon,
  color,
}: {
  label: string;
  hint: string;
  minRiskFlags: number;
  courseId?: string;
  groupId?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  const { data, isLoading, isError } = useTlAtRiskStudents({
    courseId,
    groupId,
    minRiskFlags,
    pageNumber: 1,
    pageSize: 1,
  });

  return (
    <Card className="overflow-hidden border border-t-[3px]" style={{ borderTopColor: color }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
          <div
            className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: color }}
          >
            <Icon className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
        {isLoading ? (
          <Skeleton className="h-6 w-14" />
        ) : (
          <p className="text-xl font-bold tracking-tight">
            {isError ? "—" : formatCount(data?.totalCount)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
function TeamLeaderStudentsAtRiskPage() {
  const navigate = useNavigate();
  const { courseId: initialCourseId, groupId: initialGroupId } = Route.useSearch();
  const pagination = usePaginationState({ initialPageSize: 25 });

  // Server-side facets — seeded from the deep-link search params, then owned locally (changing a
  // filter doesn't rewrite the URL; the params only set the initial context).
  const [courseFilter, setCourseFilter] = useState(initialCourseId ?? "all");
  const [groupFilter, setGroupFilter] = useState(initialGroupId ?? "all");
  const [minFlagsFilter, setMinFlagsFilter] = useState("1");

  // Current-page-only refinements (the endpoint supports neither).
  const [searchInput, setSearchInput] = useState("");
  const [activeFlagKeys, setActiveFlagKeys] = useState<string[]>([]);

  // Only the courses the caller's groups deliver — the at-risk list is group-scoped already, so the
  // org-wide assignable catalogue offered options that could only ever return an empty table.
  const { courses } = useMyGroupContent();
  const { data: groups = [] } = useMyGroups();

  const setAndReset =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      pagination.reset();
    };

  const courseId = courseFilter !== "all" ? courseFilter : undefined;
  const groupId = groupFilter !== "all" ? groupFilter : undefined;

  const params: TlAtRiskParams = useMemo(
    () => ({
      courseId,
      groupId,
      minRiskFlags: Number(minFlagsFilter),
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
    }),
    [courseId, groupId, minFlagsFilter, pagination.pageNumber, pagination.pageSize],
  );

  const { data, isLoading, isError, refetch, isFetching } = useTlAtRiskStudents(params);
  // Memoized so the `?? []` fallback doesn't hand a fresh array to the refinement useMemo below
  // on every render.
  const pageStudents: TlAtRiskStudent[] = useMemo(() => data?.items ?? [], [data]);

  const students = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    return pageStudents.filter((s) => {
      const matchesSearch =
        !term ||
        s.studentName.toLowerCase().includes(term) ||
        s.email.toLowerCase().includes(term) ||
        (s.groupName ?? "").toLowerCase().includes(term);
      const matchesFlags =
        activeFlagKeys.length === 0 ||
        activeFlagKeys.every((k) => Boolean(s[k as keyof TlAtRiskStudent]));
      return matchesSearch && matchesFlags;
    });
  }, [pageStudents, searchInput, activeFlagKeys]);

  const hasPageRefinements = !!searchInput || activeFlagKeys.length > 0;
  const hasActiveFilters =
    courseFilter !== "all" || groupFilter !== "all" || minFlagsFilter !== "1" || hasPageRefinements;

  const clearFilters = () => {
    setCourseFilter("all");
    setGroupFilter("all");
    setMinFlagsFilter("1");
    setSearchInput("");
    setActiveFlagKeys([]);
    pagination.reset();
  };

  const toggleFlag = (key: string) =>
    setActiveFlagKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  // Canonical destination — see the note on the identical helper in `exams.tsx`.
  const goToStudent = (studentId: string) => {
    navigate({
      to: "/team-leader/students/$studentId",
      params: { studentId },
      search: { tab: "exams" as const },
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-destructive/10 text-destructive shadow-inner">
              <ShieldAlert className="h-6 w-6" />
            </div>
            Students At Risk
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every student in your managed groups flagged for failing grades, low activity, failed
            checkpoints, or exam violations.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild className="gap-2 shrink-0">
          <Link to="/team-leader/exams">
            <ChevronLeft className="h-4 w-4" />
            Back to Exams
          </Link>
        </Button>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RiskSummaryCard
          label="Total At Risk"
          hint="At least one risk flag"
          minRiskFlags={1}
          courseId={courseId}
          groupId={groupId}
          icon={Users}
          color="var(--warning)"
        />
        <RiskSummaryCard
          label="Multiple Signals"
          hint="Two or more risk flags"
          minRiskFlags={2}
          courseId={courseId}
          groupId={groupId}
          icon={Layers}
          color="var(--chart-5)"
        />
        <RiskSummaryCard
          label="Critical"
          hint="Three or more risk flags"
          minRiskFlags={3}
          courseId={courseId}
          groupId={groupId}
          icon={AlertTriangle}
          color="var(--destructive)"
        />
      </div>

      {/* ── Filters ── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search this page by name, email or group..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={courseFilter} onValueChange={setAndReset(setCourseFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courses</SelectItem>
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={groupFilter} onValueChange={setAndReset(setGroupFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={minFlagsFilter} onValueChange={setAndReset(setMinFlagsFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="1+ risk flag" />
              </SelectTrigger>
              <SelectContent>
                {MIN_FLAG_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
                <FilterX className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-xs font-medium text-muted-foreground">Risk signal:</label>
            {RISK_FLAGS.map((f) => {
              const active = activeFlagKeys.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  title={f.hint}
                  onClick={() => toggleFlag(f.key)}
                  className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                    active
                      ? "bg-destructive/15 text-destructive border-destructive/40 font-medium"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
            <span className="text-[11px] text-muted-foreground ml-1">
              Search and risk-signal chips refine the current page only; course, group and flag
              count are applied server-side.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-destructive" />
                Flagged Students
              </CardTitle>
              <CardDescription>
                {data
                  ? `${data.totalCount} student${data.totalCount === 1 ? "" : "s"} flagged` +
                    (hasPageRefinements ? ` · ${students.length} shown on this page` : "")
                  : "Loading…"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={8} />
            </div>
          ) : isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Failed to load at-risk students.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : students.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-success/40" />
              {pageStudents.length === 0
                ? "No students currently at risk."
                : "No students on this page match these refinements."}
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px]">Student</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead className="text-right">Risk Score</TableHead>
                    <TableHead className="min-w-[240px]">Risk Signals</TableHead>
                    <TableHead className="text-right">Recent Fails</TableHead>
                    <TableHead className="text-right">Pass Rate</TableHead>
                    <TableHead className="text-right">Failed Checkpoints</TableHead>
                    <TableHead className="text-right">Violations</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((s) => (
                    <TableRow
                      key={s.studentId}
                      className="cursor-pointer"
                      onClick={() => goToStudent(s.studentId)}
                    >
                      <TableCell>
                        <p className="font-medium leading-tight">{s.studentName}</p>
                        <p className="text-xs text-muted-foreground leading-tight">{s.email}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{s.groupName || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={`text-[10px] tabular-nums ${riskScoreClass(s.riskScore)}`}
                        >
                          {s.riskScore} / 4
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          {RISK_FLAGS.filter((f) => Boolean(s[f.key])).map((f) => (
                            <Badge
                              key={f.key}
                              variant="outline"
                              title={f.hint}
                              className="text-[10px] bg-destructive/10 text-destructive border-destructive/30"
                            >
                              {f.label}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-destructive">
                        {formatCount(s.recentFailCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatPercent(s.recentPassRate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCount(s.failedCheckpointCount)}
                      </TableCell>
                      <TableCell className="text-right">{formatCount(s.totalViolations)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.lowActivityFlag ? formatDate(s.lastActiveAt) : "Recently active"}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data && (
            <div className="px-2">
              <PaginationControls
                pageNumber={data.pageNumber}
                pageSize={data.pageSize}
                totalPages={data.totalPages}
                totalCount={data.totalCount}
                hasNextPage={data.hasNextPage}
                hasPreviousPage={data.hasPreviousPage}
                onPageChange={pagination.setPageNumber}
                onPageSizeChange={pagination.setPageSize}
                isLoading={isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
